import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { 
  Plus, 
  Upload, 
  Download, 
  FileText, 
  Trash2,
  FileCheck,
  Loader2,
  Edit,
  Shield,
  AlertCircle,
  BookOpen,
  Scale
} from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Textarea } from '../../../components/ui/textarea';
import { Label } from '../../../components/ui/label';
import { Card, CardContent } from '../../../components/ui/card';
import { Badge } from '../../../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select';
import { toast } from 'sonner';
import { API_BASE } from '../../../config/api';

const API = `${API_BASE}/api`;

const POLICY_TYPES = [
  { value: 'attendance', label: 'Attendance Policy', icon: Shield },
  { value: 'plagiarism', label: 'Plagiarism Policy', icon: AlertCircle },
  { value: 'assessment', label: 'Assessment Policy', icon: Scale },
  { value: 'general', label: 'General Policy', icon: BookOpen },
  { value: 'custom', label: 'Custom Policy', icon: FileText }
];

export default function PoliciesStep({ programmeId, programme, token, headers, onUpdate, onRefreshStatus }) {
  const fileInputRef = useRef(null);
  const [policies, setPolicies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [showDialog, setShowDialog] = useState(false);
  const [isFileUpload, setIsFileUpload] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    policy_type: 'general'
  });
  const [selectedFile, setSelectedFile] = useState(null);

  useEffect(() => {
    if (programmeId) {
      fetchPolicies();
    } else {
      setLoading(false);
    }
  }, [programmeId]);

  const fetchPolicies = async () => {
    try {
      const res = await axios.get(`${API}/programmes/${programmeId}/policies`, { headers });
      setPolicies(res.data.policies || []);
    } catch (error) {
      console.error('Failed to fetch policies:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreatePolicy = async () => {
    if (!formData.title) {
      toast.error('Title is required');
      return;
    }

    setUploading(true);
    try {
      if (isFileUpload && selectedFile) {
        const data = new FormData();
        data.append('file', selectedFile);
        data.append('title', formData.title);
        data.append('policy_type', formData.policy_type);

        await axios.post(
          `${API}/programmes/${programmeId}/policies/upload`,
          data,
          { headers: { ...headers, 'Content-Type': 'multipart/form-data' } }
        );
      } else {
        await axios.post(
          `${API}/programmes/${programmeId}/policies`,
          formData,
          { headers }
        );
      }

      toast.success('Policy added');
      setShowDialog(false);
      setFormData({ title: '', content: '', policy_type: 'general' });
      setSelectedFile(null);
      setIsFileUpload(false);
      fetchPolicies();
      if (onRefreshStatus) onRefreshStatus();
    } catch (error) {
      console.error('Create error:', error);
      toast.error('Failed to add policy');
    } finally {
      setUploading(false);
    }
  };

  const handleDownload = async (policy) => {
    if (!policy.filename) {
      toast.error('No file attached to this policy');
      return;
    }

    try {
      const res = await axios.get(
        `${API}/programmes/${programmeId}/policies/${policy.policy_id}/download`,
        { headers, responseType: 'blob' }
      );
      
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = policy.filename;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Failed to download policy');
    }
  };

  const handleDelete = async (policyId) => {
    if (!window.confirm('Delete this policy?')) return;

    try {
      await axios.delete(`${API}/programmes/${programmeId}/policies/${policyId}`, { headers });
      toast.success('Policy deleted');
      fetchPolicies();
      if (onRefreshStatus) onRefreshStatus();
    } catch (error) {
      toast.error('Failed to delete policy');
    }
  };

  const getPolicyIcon = (type) => {
    const policyType = POLICY_TYPES.find(p => p.value === type);
    return policyType ? policyType.icon : FileText;
  };

  const getPolicyColor = (type) => {
    const colors = {
      attendance: 'text-blue-400 bg-blue-500/20',
      plagiarism: 'text-red-400 bg-red-500/20',
      assessment: 'text-amber-400 bg-amber-500/20',
      general: 'text-purple-400 bg-purple-500/20',
      custom: 'text-teal-400 bg-teal-500/20'
    };
    return colors[type] || colors.general;
  };

  if (!programmeId) {
    return (
      <div className="text-center py-12">
        <FileCheck className="h-12 w-12 text-zinc-600 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-white mb-2">Save Programme First</h3>
        <p className="text-zinc-400">Complete Step 1 to add policies</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-teal-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Description */}
      <div className="p-4 bg-purple-500/10 rounded-lg border border-purple-500/30">
        <p className="text-sm text-purple-300">
          Add programme policies such as attendance requirements, plagiarism guidelines, and assessment rules.
          You can either write the content directly or upload policy documents.
        </p>
      </div>

      {/* Add Policy Button */}
      <div className="flex justify-end">
        <Button
          onClick={() => setShowDialog(true)}
          className="bg-purple-600 hover:bg-purple-700 text-white"
        >
          <Plus className="h-4 w-4 mr-2" />
          Add Policy
        </Button>
      </div>

      {/* Policies Grid */}
      {policies.length === 0 ? (
        <div className="text-center py-12 bg-zinc-800/30 rounded-lg border border-dashed border-zinc-700">
          <FileCheck className="h-12 w-12 text-zinc-600 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-white mb-2">No policies added</h3>
          <p className="text-zinc-400 mb-4">
            Add policies to define programme rules and guidelines
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-4">
          {policies.map((policy) => {
            const Icon = getPolicyIcon(policy.policy_type);
            const colorClass = getPolicyColor(policy.policy_type);
            
            return (
              <Card key={policy.policy_id} className="bg-zinc-800/50 border-zinc-700">
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-3">
                      <div className={`p-2 rounded-lg ${colorClass.split(' ')[1]}`}>
                        <Icon className={`h-5 w-5 ${colorClass.split(' ')[0]}`} />
                      </div>
                      <div>
                        <h4 className="font-medium text-white">{policy.title}</h4>
                        <Badge variant="outline" className="text-xs mt-1 border-zinc-600 text-zinc-400">
                          {POLICY_TYPES.find(p => p.value === policy.policy_type)?.label || 'Policy'}
                        </Badge>
                        {policy.content && (
                          <p className="text-sm text-zinc-400 mt-2 line-clamp-2">
                            {policy.content}
                          </p>
                        )}
                        {policy.filename && (
                          <p className="text-xs text-zinc-500 mt-2">
                            File: {policy.filename}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      {policy.filename && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDownload(policy)}
                          className="text-zinc-400 hover:text-white"
                        >
                          <Download className="h-4 w-4" />
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDelete(policy.policy_id)}
                        className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Add Policy Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-white max-w-lg">
          <DialogHeader>
            <DialogTitle>Add Policy</DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div>
              <Label className="text-zinc-300">Policy Title *</Label>
              <Input
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="e.g., Attendance Policy"
                className="bg-zinc-800 border-zinc-700 text-white mt-1"
              />
            </div>

            <div>
              <Label className="text-zinc-300">Policy Type</Label>
              <Select
                value={formData.policy_type}
                onValueChange={(value) => setFormData({ ...formData, policy_type: value })}
              >
                <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-zinc-800 border-zinc-700">
                  {POLICY_TYPES.map((type) => (
                    <SelectItem key={type.value} value={type.value}>
                      {type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Toggle between text and file */}
            <div className="flex items-center gap-4">
              <Button
                type="button"
                variant={!isFileUpload ? "default" : "outline"}
                size="sm"
                onClick={() => setIsFileUpload(false)}
                className={!isFileUpload ? "bg-purple-600" : "border-zinc-700 text-zinc-300"}
              >
                <Edit className="h-4 w-4 mr-2" />
                Write Content
              </Button>
              <Button
                type="button"
                variant={isFileUpload ? "default" : "outline"}
                size="sm"
                onClick={() => setIsFileUpload(true)}
                className={isFileUpload ? "bg-purple-600" : "border-zinc-700 text-zinc-300"}
              >
                <Upload className="h-4 w-4 mr-2" />
                Upload File
              </Button>
            </div>

            {isFileUpload ? (
              <div>
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={(e) => setSelectedFile(e.target.files[0])}
                  accept=".pdf,.docx,.doc"
                  className="hidden"
                />
                <div 
                  className="border-2 border-dashed border-zinc-700 rounded-lg p-6 text-center cursor-pointer hover:border-purple-500/50 transition-colors"
                  onClick={() => fileInputRef.current?.click()}
                >
                  {selectedFile ? (
                    <div className="flex items-center justify-center gap-3">
                      <FileText className="h-8 w-8 text-purple-400" />
                      <span className="text-white">{selectedFile.name}</span>
                    </div>
                  ) : (
                    <>
                      <Upload className="h-8 w-8 text-zinc-500 mx-auto mb-2" />
                      <p className="text-sm text-zinc-400">Click to upload PDF or DOCX</p>
                    </>
                  )}
                </div>
              </div>
            ) : (
              <div>
                <Label className="text-zinc-300">Policy Content</Label>
                <Textarea
                  value={formData.content}
                  onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                  placeholder="Enter policy content..."
                  rows={6}
                  className="bg-zinc-800 border-zinc-700 text-white mt-1"
                />
              </div>
            )}
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowDialog(false)}
              className="border-zinc-700 text-zinc-300"
            >
              Cancel
            </Button>
            <Button
              onClick={handleCreatePolicy}
              disabled={uploading || !formData.title}
              className="bg-purple-600 hover:bg-purple-700 text-white"
            >
              {uploading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Adding...
                </>
              ) : (
                <>
                  <Plus className="h-4 w-4 mr-2" />
                  Add Policy
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
